import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { OverviewSitesRepository } from '../../application/OverviewSitesRepository'
import type { OverviewSitesResult } from '../../domain/overviewSites'

const contractType = z.enum(['RESIDENTIAL', 'COMMERCIAL', 'MULTIFAMILY'])
const category = z.enum([
  'GREEN',
  'YELLOW',
  'OPENED',
  'ORANGE',
  'DRAFT',
  'SUSPENDED',
  'EXPIRED',
  'CANCELLED',
  'NOT_INTERESTED',
])
const coordinate = z.coerce.number()

const siteSchema = z.object({
  id: z.string(),
  locationId: z.string(),
  contractId: z.string(),
  contractNumber: z.string().nullable(),
  clientId: z.string().nullable(),
  clientNumber: z.string().nullable(),
  clientName: z.string(),
  contractType,
  contractFlow: z.enum(['LEGACY', 'VNEXT']),
  category,
  statusV2: z.string().nullable(),
  signedAt: z.string().nullable(),
  season: z.string().nullable(),
  address: z.string(),
  city: z.string().nullable(),
  order: z.coerce.number().int(),
  contractSiteCount: z.coerce.number().int(),
  latitude: coordinate,
  longitude: coordinate,
  positionAdjustedAt: z.string().nullable(),
  routeId: z.string().nullable(),
  contractPriceCents: z.coerce.number().int(),
  locationPriceCents: z.coerce.number().int().nullable(),
  parkingSpaces: z.coerce.number().int().nullable(),
  drivewayCapacity: z.coerce.number().int().nullable(),
  // Absent avant `20261204010000` : en service.
  serviceStatus: z.enum(['ACTIVE', 'SUSPENDED']).default('ACTIVE'),
})

const requestSchema = z.object({
  id: z.string(),
  leadId: z.string(),
  number: z.string().nullable(),
  name: z.string(),
  address: z.string().nullable(),
  latitude: coordinate,
  longitude: coordinate,
  status: z.enum([
    'NEW',
    'TO_CONTACT',
    'CALLBACK',
    'NO_ANSWER',
    'INTERESTED',
    'CONTACTED_NOT_INTERESTED',
    'CONVERTED',
  ]),
  origin: z.string().nullable(),
  serviceType: z.string().nullable(),
  createdAt: z.string(),
  convertedContractId: z.string().nullable(),
  convertedContractNumber: z.string().nullable(),
})

const unpositionedSchema = z.object({
  id: z.string(),
  kind: z.enum(['SITE', 'CONTRACT', 'REQUEST']),
  reason: z.enum(['NO_COORDINATES', 'NO_SERVICE_ADDRESS']),
  label: z.string().nullable(),
  address: z.string().nullable(),
  contractId: z.string().nullish(),
  contractNumber: z.string().nullish(),
  contractType: contractType.nullish(),
  category: category.nullish(),
  leadId: z.string().nullish(),
  leadNumber: z.string().nullish(),
})

const routeSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
  category: z.enum(['RESIDENTIAL', 'LARGE_SITE']),
  sector: z.string().nullable(),
})

const resultSchema = z.object({
  season: z.string().nullable(),
  visible: z.object({ contracts: z.boolean(), requests: z.boolean(), routes: z.boolean() }),
  sites: z.array(siteSchema),
  requests: z.array(requestSchema),
  unpositioned: z.array(unpositionedSchema),
  routes: z.array(routeSchema).nullable(),
  computedAt: z.string(),
})

export function parseOverviewSites(data: unknown): OverviewSitesResult {
  const parsed = resultSchema.safeParse(data)
  if (!parsed.success) throw new Error('OVERVIEW_SITES_RESPONSE_INVALID')
  const raw = parsed.data
  return {
    ...raw,
    unpositioned: raw.unpositioned.map((row) => ({
      id: row.id,
      kind: row.kind,
      reason: row.reason,
      label: row.label ?? 'Sans nom',
      address: row.address,
      contractId: row.contractId ?? null,
      contractNumber: row.contractNumber ?? null,
      contractType: row.contractType ?? null,
      category: row.category ?? null,
      leadId: row.leadId ?? null,
      leadNumber: row.leadNumber ?? null,
    })),
    source: 'supabase',
  }
}

/** Aucun repli fixture : une erreur reste une erreur affichée, jamais de faux Contrats sur la carte. */
export function createSupabaseOverviewSitesRepository(client: AppSupabaseClient): OverviewSitesRepository {
  return {
    async load(season) {
      const rpcClient = client as unknown as SupabaseClient
      const response = await rpcClient.rpc('overview_sites_v3', { p_season: season })
      if (response.error) throw new Error(response.error.message || 'OVERVIEW_SITES_FAILED')
      return parseOverviewSites(response.data)
    },
  }
}
